import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Mail, Trash2, UserCheck, Clock } from "lucide-react";

const ROLE_LABEL = { viewer: "Viewer", commenter: "Commenter", editor: "Editor" };

export default function HiveCoKeepers({ hiveId, isOwner }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/hives/${hiveId}/members`);
      setMembers(data);
    } catch {
      setMembers([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [hiveId]);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post(`/hives/${hiveId}/invites`, { email, role });
      toast.success(`Invited ${email} as ${ROLE_LABEL[role]}`);
      setEmail(""); setRole("viewer");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally { setInviting(false); }
  };

  const changeRole = async (m, r) => {
    try {
      await api.patch(`/hives/${hiveId}/members/${m.id}`, { role: r });
      toast.success(`${m.invite_email} is now a ${ROLE_LABEL[r]}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const revoke = async (m) => {
    if (!window.confirm(`Remove ${m.invite_email} from this hive?`)) return;
    try {
      await api.delete(`/hives/${hiveId}/members/${m.id}`);
      toast.success("Access removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="co-keepers-section">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-stone-500" />
        <h2 className="font-display text-lg font-medium">Co-keepers</h2>
      </div>

      {loading ? <p className="text-sm text-stone-500">Loading…</p> : members.length === 0 ? (
        <p className="text-sm text-stone-500">No co-keepers yet.</p>
      ) : (
        <ul className="space-y-2 mb-4">
          {members.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-stone-50" data-testid={`member-row-${m.id}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                {m.status === "pending" ? (
                  <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                ) : m.member_avatar ? (
                  <img src={m.member_avatar} alt="" className="w-7 h-7 rounded-full object-cover shrink-0 border border-stone-200" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 grid place-items-center text-xs font-semibold shrink-0">
                    {(m.member_name || m.invite_email)[0]?.toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{m.member_name || m.invite_email}</div>
                  <div className="text-xs text-stone-500 truncate">
                    {m.status === "pending" ? "Pending — emailed invite" : m.invite_email}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isOwner ? (
                  <>
                    <Select value={m.role} onValueChange={(r) => changeRole(m, r)} disabled={m.status !== "accepted"}>
                      <SelectTrigger className="w-[110px] h-7 text-xs" data-testid={`member-role-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" onClick={() => revoke(m)} className="text-stone-400 hover:text-red-600 h-7 w-7" data-testid={`member-revoke-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <span className="text-xs uppercase tracking-[0.12em] text-stone-500">{ROLE_LABEL[m.role]}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {isOwner && (
        <form onSubmit={invite} className="grid grid-cols-[1fr_140px_auto] gap-2 items-end" data-testid="invite-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.12em] text-stone-500">Invite by email</Label>
            <div className="relative mt-1">
              <Mail className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="bee@example.com"
                className="pl-7 text-sm"
                data-testid="invite-email-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.12em] text-stone-500">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1 text-sm" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={inviting || !email} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="invite-submit-button">
            <UserCheck className="w-4 h-4 mr-1.5" /> Invite
          </Button>
        </form>
      )}
    </div>
  );
}
